/**
 * =============================================================================
 * Hook: useAffiliateReferrals
 * Versão: 2.1.0 (Suporte a filtro de período)
 *
 * Mudanças v2.1.0:
 *   - Aceita opcionalmente `{ startDate, endDate }` que são repassados como
 *     `p_period_start` / `p_period_end` à RPC `get_affiliate_referrals_page`.
 *   - Sem args, comportamento atual (todas as indicações do afiliado).
 *
 * v2.0.0:
 *   - Substitui o SELECT direto por RPC. Leads ficam mascarados.
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useAffiliateReferrals');

export type ReferralStatus = Database['public']['Enums']['referral_status'];

/**
 * Linha exibida na UI. Mantém os mesmos nomes de campo da v1, mas:
 *   - `lead_email`/`lead_phone` agora vêm MASCARADOS do servidor.
 *   - Campos `campaign` e `program` continuam aninhados para compatibilidade.
 */
export interface AffiliateReferralRow {
  id: string;
  status: ReferralStatus;
  lead_name: string | null;
  lead_email: string | null;
  lead_phone: string | null;
  lead_source: string | null;
  conversion_value: number | null;
  /** v1.1.0 — Soma das comissões (pending+approved+paid) desta indicação. */
  commission_value: number | null;
  created_at: string;
  qualified_at: string | null;
  converted_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  campaign_id: string | null;
  campaign: { id: string; name: string } | null;
  program_id: string;
  program: { id: string; name: string } | null;
}

interface RpcRow {
  id_short: string;
  status: string;
  lead_name: string | null;
  lead_email_masked: string | null;
  lead_phone_masked: string | null;
  lead_source: string | null;
  conversion_value: number | null;
  commission_value: number | null;
  created_at_iso: string;
  qualified_at: string | null;
  converted_at: string | null;
  lost_at: string | null;
  lost_reason: string | null;
  campaign_id_short: string;
  campaign_name: string | null;
  program_id_short: string;
  program_name: string | null;
  program_id: string;
}

/** Parâmetros opcionais de filtro do hook. */
export interface UseAffiliateReferralsParams {
  startDate?: Date;
  endDate?: Date;
}

export function useAffiliateReferrals(params: UseAffiliateReferralsParams = {}) {
  const { affiliate, ready } = useAffiliateAuth();
  const { startDate, endDate } = params;

  // Chaves estáveis para o cache
  const startKey = startDate ? startDate.toISOString() : null;
  const endKey = endDate ? endDate.toISOString() : null;

  return useQuery({
    queryKey: ['affiliate-referrals', affiliate?.id, startKey, endKey],
    enabled: ready && !!affiliate?.id,
    queryFn: async (): Promise<AffiliateReferralRow[]> => {
      const { data, error } = await supabase.rpc('get_affiliate_referrals_page', {
        p_status: null,
        p_program_id: null,
        p_period_start: startKey,
        p_period_end: endKey,
        p_limit: 200,
        p_offset: 0,
      });

      if (error) {
        log.error('Falha ao listar indicações via RPC', error);
        throw error;
      }

      const payload = data as unknown as { rows: RpcRow[]; total: number };
      const rows = payload?.rows ?? [];

      // Adapta para o shape que a UI espera (campaign/program aninhados).
      return rows.map<AffiliateReferralRow>((r) => ({
        id: r.id_short,
        status: r.status as ReferralStatus,
        lead_name: r.lead_name,
        lead_email: r.lead_email_masked,
        lead_phone: r.lead_phone_masked,
        lead_source: r.lead_source,
        conversion_value: r.conversion_value,
        commission_value: r.commission_value ?? 0,
        created_at: r.created_at_iso,
        qualified_at: r.qualified_at,
        converted_at: r.converted_at,
        lost_at: r.lost_at,
        lost_reason: r.lost_reason,
        campaign_id: r.campaign_id_short || null,
        campaign: r.campaign_name
          ? { id: r.campaign_id_short, name: r.campaign_name }
          : null,
        program_id: r.program_id,
        program: r.program_name
          ? { id: r.program_id_short, name: r.program_name }
          : null,
      }));
    },
    staleTime: 30_000,
  });
}
